import { useEffect, useState } from 'react'
import type { CatalogPreset } from '~/presets/catalogPresets'
import type { ColorName } from '~/utils/colorDictionary'
import { buildCatalogQuoteArgs, catalogSku } from '~/utils/catalogQuoteBody'
import {
  findChipboardIdByColorName,
  getFurnitureBySkuCached,
} from '~/utils/furnitureBySku'
import { fetchPricingQuote } from '~/utils/pricingQuote'

export interface CatalogPriceResult {
  /** Live quote total for the currently selected colour. */
  price: number | null
  loading: boolean
  /** True for types with no quote endpoint (table) — caller uses static price. */
  isStatic: boolean
}

/**
 * Price a catalog card live via /api/pricing/quote for the SELECTED colour. The
 * quote re-fires whenever the colour changes (each colour maps to a different
 * chipboard, hence a different price). Prices are never hard-coded: every value
 * comes from a fresh quote. Returns isStatic=true for types without a pricing
 * endpoint (table) so the card can fall back to the static price.
 */
export function useCatalogPrice(
  preset: CatalogPreset,
  selectedColor: ColorName
): CatalogPriceResult {
  const sku = catalogSku(preset)
  const [price, setPrice] = useState<number | null>(null)
  const [loading, setLoading] = useState(sku !== null)

  useEffect(() => {
    if (!sku) return
    let cancelled = false

    getFurnitureBySkuCached(sku).then(async (furniture) => {
      if (cancelled) return
      const chipboardId = findChipboardIdByColorName(furniture, selectedColor)
      const args = buildCatalogQuoteArgs(preset, chipboardId)
      if (!args) {
        if (!cancelled) setLoading(false)
        return
      }
      const quote = await fetchPricingQuote(args)
      if (cancelled) return
      // Exclude opt-in services (delivery/assembly) — they're toggled on the
      // product page, not in the catalog "de la" price.
      const amountOf = (groupSku: string) =>
        quote?.breakdown.find((b) => b.groupSku === groupSku)?.amount ?? 0
      const base =
        quote != null
          ? quote.total - amountOf('delivery') - amountOf('assembly')
          : null
      setPrice(base)
      setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [sku, selectedColor, preset])

  return { price, loading, isStatic: sku === null }
}